import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSDocumentVerificationCompleteData,
  type EnrollmentHYCSDocumentVerificationCompleteSubmitInput,
  type EnrollmentHYCSDocumentVerificationCompleteSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDocumentVerificationCompleteData>
 * @example const response = await getEnrollmentHYCSDocumentVerificationCompleteData()
 */
export const getEnrollmentHYCSDocumentVerificationCompleteData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSDocumentVerificationCompleteData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSDocumentVerificationCompleteData',
      query: `mutation getEnrollmentHYCSDocumentVerificationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDocumentVerificationCompleteData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSDocumentVerificationCompleteData = createQueryHook<
  undefined,
  EnrollmentHYCSDocumentVerificationCompleteData
>(getEnrollmentHYCSDocumentVerificationCompleteData)

/**
 * Submit Enrollment HYCS DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDocumentVerificationCompleteSubmitOutput>
 * @example const response = await submitEnrollmentHYCSDocumentVerificationCompleteData();
 */
export const submitEnrollmentHYCSDocumentVerificationCompleteData = async (
  enrollmentDocumentVerificationCompleteSubmitInput: EnrollmentHYCSDocumentVerificationCompleteSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSDocumentVerificationCompleteSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSDocumentVerificationCompleteData',
      query: `mutation submitEnrollmentHYCSDocumentVerificationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocumentVerificationCompleteSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDocumentVerificationCompleteSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSDocumentVerificationCompleteData = createMutationHook<
  EnrollmentHYCSDocumentVerificationCompleteSubmitInput,
  EnrollmentHYCSDocumentVerificationCompleteSubmitOutput
>(submitEnrollmentHYCSDocumentVerificationCompleteData)
